import { useEffect, useState } from 'react';
import { Badge, Button, Card, Flex, Spinner, Stack, Text } from '@chakra-ui/react';
import { format, subDays } from 'date-fns';
import { useParams } from 'react-router-dom';
import api from '@/utils/api';
import { PlanScope, PlannerPlan } from '@/lib/types';
import { unwrap } from '@/pages/planner/api';
import { draftFromPast } from '@/pages/planner/draft';
import { PlannerAction } from '@/pages/planner/planner-machine';
import { toaster } from '@/components/ui/toaster';

interface ModPastScreenProps {
  date: string;
  // The day slot the copy will fill on `date` — carried from the choice
  // screen onto the draft. The source list mixes workspace plans and the
  // caller's own past "My Plans"; either kind may be copied into either slot.
  scope: PlanScope;
  dispatch: (action: PlannerAction) => void;
}

// v1 heuristic: a fixed 90-day lookback ending today, so a plan saved earlier
// the same day is immediately reusable (future plans still excluded). This is a
// one-off, component-local api call — deliberately NOT routed through
// usePlannerPlansStore.loadRange, since that store replaces `items` wholesale
// keyed by whatever range was last loaded; reusing it here would clobber the
// calendar strip's currently-loaded week/month range the moment this screen
// mounts (and vice versa when the user navigates back to the calendar).
export default function ModPastScreen({ date, scope, dispatch }: ModPastScreenProps) {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const [pastPlans, setPastPlans] = useState<PlannerPlan[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    setIsLoading(true);
    const to = format(new Date(), 'yyyy-MM-dd');
    const from = format(subDays(new Date(), 90), 'yyyy-MM-dd');
    api
      .get(`practice-plans/workspace/${workspaceId}/range/${from}/${to}`)
      .then((response) => {
        if (cancelled) return;
        const plans = unwrap<PlannerPlan[]>(response) ?? [];
        // Newest first.
        setPastPlans([...plans].sort((a, b) => (a.plan_date < b.plan_date ? 1 : -1)));
      })
      .catch((error: any) => {
        if (cancelled) return;
        toaster.create({
          type: 'error',
          title: error.response?.data?.error ?? 'Failed to load past plans',
        });
        setPastPlans([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const handleUsePlan = (plan: PlannerPlan) => {
    dispatch({ type: 'USE_PAST_PLAN', draft: draftFromPast(plan, date, scope) });
  };

  return (
    <Stack gap={4}>
      <Text textStyle='eyebrow'>Use & modify past plan</Text>
      <Text fontSize='sm' color='fg.muted'>
        Plans from the last 90 days. Choosing one copies it onto{' '}
        {format(new Date(`${date}T00:00:00`), 'MMMM d, yyyy')} for editing.
      </Text>

      {isLoading && (
        <Flex justify='center' py={8}>
          <Spinner color='fg.subtle' />
        </Flex>
      )}

      {!isLoading && pastPlans?.length === 0 && (
        <Text fontSize='sm' color='fg.subtle'>
          No past plans found in the last 90 days.
        </Text>
      )}

      {!isLoading &&
        pastPlans?.map((plan) => (
          <Card.Root key={plan.id}>
            <Card.Body>
              <Flex align='center' justify='space-between' gap={3} flexWrap='wrap'>
                <Stack gap={0.5}>
                  <Flex align='center' gap={2}>
                    <Text fontWeight='600' color='fg.strong'>
                      {plan.name}
                    </Text>
                    {plan.scope === 'personal' && (
                      <Badge variant='outline' size='sm'>
                        My plan
                      </Badge>
                    )}
                  </Flex>
                  <Text fontSize='sm' color='fg.muted'>
                    Used {format(new Date(`${plan.plan_date.substring(0, 10)}T00:00:00`), 'MMM d')} ·{' '}
                    {plan.duration_minutes} min · {plan.sections.length} section
                    {plan.sections.length === 1 ? '' : 's'}
                  </Text>
                </Stack>
                <Button colorPalette='brand' size='sm' onClick={() => handleUsePlan(plan)}>
                  Use plan
                </Button>
              </Flex>
            </Card.Body>
          </Card.Root>
        ))}
    </Stack>
  );
}
